import type { Address } from "@core/schema/Address.js";
import type { Country } from "@core/schema/Country.js";
import type { CountrySettings } from "@core/schema/CountrySettings.js";
import type { Currency } from "@core/schema/Currency.js";
import { DeliveryType } from "@core/schema/DeliveryType.js";
import type { EconomicSubjectVatType } from "@core/schema/EconomicSubjectVatType.js";
import { PaymentType } from "@core/schema/PaymentType.js";
import type { calculatePriceOfOrder } from "@core/schema/utils/calculatePriceOfOrder.js";
import { isCountryInEuropeanUnion } from "@core/schema/utils/isCountryInEuropeanUnion.js";
import { shouldApplyTax } from "@core/schema/utils/shouldApplyTax.js";
import { isPplDeliveryType } from "@core/utils/isPplDeliveryType.js";
import type { OrderAfterOverview } from "./CartType.js";

export function getPriceDataFromOrder(
	order: OrderAfterOverview,
	countrySettings: CountrySettings[],
	currency: Currency,
	destinationCountry: Country | "outsideEu",
	vatType: EconomicSubjectVatType,
): { order: Parameters<typeof calculatePriceOfOrder>[0]; applyTax: boolean } {
	const country =
		destinationCountry === "outsideEu"
			? countrySettings.find(({ country }) => !isCountryInEuropeanUnion(country))?.country
			: destinationCountry;

	if (country === undefined) {
		throw new Error("Country settings for the destination country not found.");
	}

	const billingAddress: Address = {
		country,
		city: "",
		street: "",
		firstName: "",
		lastName: "",
		zip: "",
		...order.billingAddress,
		pplDeliveryPoint: undefined,
	};

	const deliveryAddress: Address = {
		...billingAddress,
		...order.deliveryAddress,
		pplDeliveryPoint:
			isPplDeliveryType(order.deliveryType) && order.deliveryAddress?.pplDeliveryPoint
				? { name: "", code: "", ...order.deliveryAddress.pplDeliveryPoint }
				: undefined,
	};

	return {
		order: {
			deliveryType: DeliveryType.DeliveryToAddress,
			...order,
			billingAddress,
			deliveryAddress,
			payment: {
				type: PaymentType.GoPayPaymentCard,
				currency,
				...order.payment,
			},
		},
		applyTax: shouldApplyTax(deliveryAddress.country, vatType),
	};
}
